// The animated finish on the Overflow wordmark: his pearl ropes are aimed onto the letters
// and drip down the page, while her squirt arcs onto the Reviewers block (or off the right
// edge). It draws on full-screen layers beneath the page content (pointer-events off) so it has room; positions are kept relative to
// the word so everything moves with it when the page scrolls. Two canvases so only the
// pearl gets the "goo" filter that melts drops together.
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

type RGB = readonly [number, number, number];
const PEARL: RGB = [247, 241, 234];
const AQUA: RGB = [159, 220, 255];
const MIST: RGB = [232, 248, 255];

// x/y are relative to the word's top-left corner
interface Drop { x: number; y: number; px?: number; py?: number; vx: number; vy: number; r: number; color: RGB; a: number; landY: number; drip: number; flat: number; mist?: boolean; dead?: boolean }
interface Stain { x: number; y: number; r: number; color: RGB; a: number; flat: number; len: number; max: number; speed: number }

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const rad = (d: number) => (d * Math.PI) / 180;

const CYCLE = 10000;      // one play every 10 seconds
const FADE = 2200;        // fades from about 5.5s so it never sits over the page for long
const FADE_START = 5500;
const GRAVITY = 1500;

export const OverflowFinish: React.FC<{ textClassName?: string }> = ({ textClassName = 'text-[44px]' }) => {
  const word = useRef<HTMLSpanElement>(null);
  const pearlCanvas = useRef<HTMLCanvasElement>(null);
  const aquaCanvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const canvases = [pearlCanvas.current, aquaCanvas.current];
    if (!canvases[0] || !canvases[1] || !word.current) return;
    const ctxs = canvases.map(c => c!.getContext('2d')!);
    let vw = 0, vh = 0;
    let drops: Drop[] = [], stains: Stain[] = [];
    let t0 = performance.now(), last = t0, raf = 0;

    const resize = () => {
      vw = window.innerWidth; vh = window.innerHeight;
      const dpr = window.devicePixelRatio || 1;
      for (const c of canvases) { c!.width = vw * dpr; c!.height = vh * dpr; }
      ctxs.forEach(x => x.setTransform(dpr, 0, 0, dpr, 0, 0));
    };
    resize();
    window.addEventListener('resize', resize);

    const box = () => word.current!.getBoundingClientRect();
    // Spout: just past the end of the word, a little below the middle
    const spout = (b: DOMRect) => ({ x: b.width + 8, y: b.height * 0.58 });
    // His ropes start further back, well to the right of the word, so they fly further
    const pearlSpout = (b: DOMRect) => ({ x: b.width + Math.max(40, Math.min(150, vw - b.right - 30)), y: b.height * 0.45 });
    // Her squirt aims at whatever is marked data-squirt-target (the Reviewers block on home)
    const target = () => document.querySelector<HTMLElement>('[data-squirt-target]')?.getBoundingClientRect() ?? null;

    const add = (d: Partial<Drop> & { x: number; y: number; vx: number; vy: number }) =>
      drops.push({ r: 3, color: PEARL, a: 1, landY: 0, drip: 0, flat: 0, ...d });

    // Aim a drop from the spout so it comes down exactly on (tx, ty) after `flight` seconds
    const aimAt = (o: { x: number; y: number }, tx: number, ty: number, flight: number, d: Partial<Drop>) => {
      add({ x: o.x, y: o.y, vx: (tx - o.x) / flight, vy: (ty - o.y) / flight - 0.5 * GRAVITY * flight, landY: ty, ...d });
    };

    const land = (p: Drop, b: DOMRect) => {
      const y = Math.min(p.y, p.landY);
      const roomBelow = vh - (b.top + y) - 16;
      const isPearl = p.color === PEARL;
      stains.push({
        x: p.x, y, r: p.r * rand(1, 1.35), color: p.color, a: p.a, flat: p.flat, len: 0,
        // his runs drip a long way down the page; hers mostly spread
        max: isPearl && Math.random() < p.drip ? rand(30, Math.max(50, Math.min(420, roomBelow))) : 0,
        speed: rand(24, 60),
      });
      const extra = p.r > 2.6 ? Math.round(rand(0, 1.4)) : 0;
      for (let i = 0; i < extra; i++) stains.push({ x: p.x + rand(-1, 1) * p.r * 3, y: y + rand(-1, 1) * p.r * 2, r: p.r * rand(0.25, 0.55), color: p.color, a: p.a, flat: 0, len: 0, max: 0, speed: 0 });
    };

    const emit = (t: number, b: DOMRect) => {
      const s = Math.max(0.6, Math.min(1.15, b.height / 50));
      // His ropes: four pulses, each laid as a streak across the letters, right to left
      [0, 420, 800, 1120].forEach((start, i) => {
        const dur = 260;
        if (t >= start && t < start + dur) {
          const progress = (t - start) / dur;
          const centre = b.width * (0.86 - i * 0.22);
          for (let n = 0; n < (Math.random() < 0.35 ? 2 : 1); n++) {
            const tx = centre + b.width * 0.14 - progress * b.width * 0.3 + rand(-3, 3);
            const ty = b.height * rand(0.3, 0.75);
            aimAt(pearlSpout(b), Math.max(4, tx), ty, 0.5 + i * 0.05, { r: (rand(2.6, 3.5) - i * 0.25) * s, drip: 0.4 });
          }
        }
      });
      // Her squirt: thick pulsing jets. With a target on the page they land on it; otherwise they
      // arc right across the screen and off the edge
      const o = spout(b);
      const tr = target();
      if (tr) {
        [180, 620, 1020, 1380, 1720].forEach((start, i) => {
          const dur = 300;
          if (t >= start && t < start + dur) {
            const progress = (t - start) / dur;
            // each pulse sweeps across the block, landing a little further down than the last
            const tx = tr.left - b.left + tr.width * (0.08 + progress * 0.84) + rand(-6, 6);
            const ty = tr.top - b.top + tr.height * Math.min(0.9, 0.1 + i * 0.19) + rand(-8, 8);
            const flight = 0.5 + Math.hypot(tx - o.x, ty - o.y) / 2400;
            for (let n = 0; n < 3; n++) aimAt(o, tx + rand(-4, 4), ty + rand(-3, 3), flight * rand(0.97, 1.03), { color: AQUA, a: rand(0.6, 0.9), r: rand(2.6, 4.2) * s, flat: 0.75 });
            for (let n = 0; n < 2; n++) aimAt(o, tx + rand(-40, 40), ty + rand(-30, 10), flight * rand(0.8, 1.1), { color: MIST, a: 0.5, r: rand(0.6, 1.3), mist: true });
          }
        });
        return;
      }
      const reach = Math.max(220, (vw - (b.left + o.x)) * 1.25);
      [180, 640, 1060, 1440, 1780].forEach((start, i) => {
        const dur = 280;
        if (t >= start && t < start + dur) {
          const angle = 34 - i * 3;                          // degrees above horizontal
          const range = reach * (1.1 - i * 0.04) * rand(0.96, 1.04);
          const v = Math.sqrt((range * GRAVITY) / Math.sin(rad(2 * angle)));
          for (let n = 0; n < 3; n++) {
            const a = rad(-(angle + rand(-1.5, 1.5)));
            add({ x: o.x, y: o.y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, color: AQUA, a: rand(0.6, 0.9), r: rand(2.6, 4.2) * s, flat: 0.75, landY: b.height * 0.92 });
          }
          for (let n = 0; n < 2; n++) {
            const a = rad(-rand(angle - 12, angle + 14));
            add({ x: o.x, y: o.y, vx: Math.cos(a) * v * rand(0.4, 0.9), vy: Math.sin(a) * v * rand(0.4, 0.9), color: MIST, a: 0.5, r: rand(0.6, 1.2), mist: true, landY: b.height * 0.92 });
          }
        }
      });
    };

    const draw = (fade: number, b: DOMRect) => {
      ctxs.forEach(x => x.clearRect(0, 0, vw, vh));
      const ox = b.left, oy = b.top;
      for (const st of stains) {
        const x = ctxs[st.color === PEARL ? 0 : 1]!;
        x.fillStyle = `rgba(${st.color.join(',')},${st.a * fade})`;
        x.beginPath(); x.ellipse(ox + st.x, oy + st.y, st.r * (1 + st.flat), st.r * (1 - st.flat * 0.6), 0, 0, Math.PI * 2); x.fill();
        if (st.len > 0) {
          // a run that narrows as it goes, with a bead at the end
          const top = st.r * 0.5, tip = Math.max(1, st.r * 0.28);
          x.beginPath();
          x.moveTo(ox + st.x - top / 2, oy + st.y);
          x.lineTo(ox + st.x + top / 2, oy + st.y);
          x.lineTo(ox + st.x + tip / 2, oy + st.y + st.len);
          x.lineTo(ox + st.x - tip / 2, oy + st.y + st.len);
          x.closePath(); x.fill();
          x.beginPath(); x.arc(ox + st.x, oy + st.y + st.len, tip * 1.25, 0, Math.PI * 2); x.fill();
        }
      }
      for (const p of drops) {
        const x = ctxs[p.color === PEARL ? 0 : 1]!;
        x.strokeStyle = x.fillStyle = `rgba(${p.color.join(',')},${p.a * fade})`;
        if (p.mist || p.px === undefined) { x.beginPath(); x.arc(ox + p.x, oy + p.y, p.r, 0, Math.PI * 2); x.fill(); continue; }
        x.lineCap = 'round'; x.lineWidth = p.r * 2;
        x.beginPath(); x.moveTo(ox + p.px - (p.x - p.px) * 1.6, oy + p.py! - (p.y - p.py!) * 1.6); x.lineTo(ox + p.x, oy + p.y); x.stroke();
      }
    };

    const tick = (now: number) => {
      const dt = Math.min(0.033, (now - last) / 1000); last = now;
      let t = now - t0;
      if (t > CYCLE) { t0 = now; t = 0; drops = []; stains = []; }
      const b = box();
      emit(t, b);
      for (const p of drops) {
        p.px = p.x; p.py = p.y;
        p.vy += GRAVITY * dt;
        p.x += p.vx * dt; p.y += p.vy * dt;
        if (p.vy > 0 && p.y >= p.landY) { p.dead = true; if (!p.mist) land(p, b); }
        else if (b.left + p.x > vw + 40 || b.top + p.y > vh + 40) p.dead = true;
      }
      drops = drops.filter(p => !p.dead);
      for (const st of stains) if (st.len < st.max) { st.speed *= 1 + 0.25 * dt; st.len = Math.min(st.max, st.len + st.speed * dt); }
      draw(t < FADE_START ? 1 : Math.max(0, 1 - (t - FADE_START) / FADE), b);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);

  // Both run beneath the page (z-[5] sits between the fluid background at z-0 and page content
  // at z-10), so the word, text and cards stay on top
  const layer = 'fixed inset-0 w-screen h-screen pointer-events-none';
  return (
    <>
      <span ref={word} className={`relative inline-block font-display font-medium tracking-tight text-ink leading-none [text-shadow:0_0_2px_#121014,0_1px_8px_rgba(18,16,20,0.85)] ${textClassName}`}>
        Over<span className="italic text-her">flow</span>
      </span>
      {createPortal(
        <>
          <canvas ref={pearlCanvas} aria-hidden="true" className={`${layer} z-[5] overflow-goo`} />
          <canvas ref={aquaCanvas} aria-hidden="true" className={`${layer} z-[5]`} />
        </>,
        document.body,
      )}
    </>
  );
};
